import { useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client/react';
import { createReferralClient, type ReferralClient } from '@optiprime/referral-sdk';
import { CLEAR_REFERRAL_PROMPT } from '@scio3/graphql-client';
import { getTokenString } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Memoized referral-engine SDK client. The widget calls `client.me()/invite()/…`
 * against the engine at `VITE_REFERRAL_API_URL` (localhost:9104 in dev) — NOT
 * scio3 GraphQL. Auth uses the same Firebase ID token as Apollo, via
 * `getTokenString()` (awaits `authInitialized`, refreshes if near-expiry).
 *
 * - The client identity is stable across renders (memoized on baseUrl) so the
 *   widget never remounts and we avoid request loops.
 * - `getToken` coerces a signed-out `null` to `''` so it never throws: the
 *   public `validate()` needs no token, and `me()/invite()` 401 cleanly rather
 *   than crashing if somehow called while signed out.
 */
export function useReferralClient(): ReferralClient {
  // VITE_REFERRAL_API_URL is unset in every env except qa3d/prod (its type says
  // `string` but it's `undefined` at runtime when absent). This hook runs in
  // ReferralWidgetMount BEFORE the `entryEnabled` gate, so it must never crash:
  // coerce a missing URL to '' (the SDK's baseUrl.replace then no-ops). The
  // widget is still gated off in those envs, so the empty-base client is unused.
  const baseUrl = import.meta.env.VITE_REFERRAL_API_URL ?? '';

  return useMemo(
    () =>
      createReferralClient({
        baseUrl,
        getToken: async () => (await getTokenString()) ?? '',
      }),
    [baseUrl],
  );
}

export interface ReferralLink {
  referralCode?: string;
  referralLink?: string;
}

/**
 * The signed-in user's own referral code + link, read from the local `Me`
 * mirror in auth-store (populated by ME_QUERY at bootstrap). This is the cheap
 * path the widget's `preloadedCode` uses — no engine round-trip needed to show
 * the referrer their link. Returns empties until `me` has loaded.
 */
export function useReferralLink(): ReferralLink {
  const referralCode = useAuthStore((s) => s.me?.referralCode);
  const referralLink = useAuthStore((s) => s.me?.referralLink);
  return { referralCode, referralLink };
}

export interface ReferralPrompt {
  /** Event type the user just hit (`first_paid` / `first_expansion`), or undefined. */
  pending?: string;
  /** Persist "shown" server-side + optimistically clear the local Me mirror. */
  clearPrompt: () => void;
}

/**
 * The referral expansion prompt (server-driven, once per event type). open-api
 * sets `Me.referralPromptPending` when the user hits an expansion moment; the
 * widget opens the refer modal once when `pending` is set, then calls
 * `clearPrompt`, which fires `clearReferralPrompt` (records the shown type so it
 * never re-prompts) and optimistically clears the local mirror so a remount
 * doesn't re-open it.
 */
export function useReferralPrompt(): ReferralPrompt {
  const pending = useAuthStore((s) => s.me?.referralPromptPending);
  const setMe = useAuthStore((s) => s.setMe);
  const [clearMutation] = useMutation(CLEAR_REFERRAL_PROMPT);
  const clearPrompt = useCallback(() => {
    void clearMutation().catch(() => {});
    const me = useAuthStore.getState().me;
    if (me?.referralPromptPending) setMe({ ...me, referralPromptPending: undefined });
  }, [clearMutation, setMe]);
  return { pending, clearPrompt };
}
